import { apiClient as centralizedApiClient } from './apiClient';
import { AuthError, handleError } from '../utils/errors';
import { authLogger } from '../utils/loggerInstance';
import { AUTH_CONFIG } from '../config/auth';
import { ENV } from '../config/env';
import {
  AuthProvider,
  AuthResponse,
  OAuthAuthProvider,
  supportsOAuth,
} from './auth/providerInterface';
import { authProvider } from './auth/authFactory';
import {
  registerTokenRefreshCallback,
  registerAuthErrorCallback,
  registerCsrfTokenGetter,
  registerAccessTokenGetter,
  registerAccessTokenFetcher,
  notifyAuthStateChange,
  triggerAuthError,
} from './tokenRefreshRegistry';
import { securityEventTracker } from './securityEventTracker';
import { shouldSuppressRedirect } from '../utils/navigationRedirectHelpers';
import { getCsrfTokenFromDocumentCookie } from '../utils/cookieUtils';
import { buildOAuthSignInFailureMessage } from '../utils/authOAuthErrorHandler';

const API_BASE_URL = AUTH_CONFIG.API_BASE_URL;
if (API_BASE_URL === undefined || API_BASE_URL === null)
  throw new Error('API_BASE_URL is required');

/** Shared axios config for auth endpoints (shorter timeout than default apiClient). */
const AUTH_HTTP_OPTIONS = { timeout: AUTH_CONFIG.AUTH_HTTP_TIMEOUT_MS };

// Auth error callback type and storage
type AuthErrorCallback = (currentPath: string) => void;
let authErrorCallback: AuthErrorCallback | null = null;

// Constants for token refresh failure handling
const MAX_REFRESH_FAILURES = 3;
const REFRESH_FAILURE_KEY = 'auth_refresh_failures';

/**
 * Set callback function to be called when authentication errors occur
 */
export const setAuthErrorCallback = (callback: AuthErrorCallback): void => {
  authErrorCallback = callback;
  authLogger.info('Auth error callback registered');
};

/**
 * Call the registered auth error callback if it exists
 * (Backward compatibility - now delegates to registry)
 */
export const callAuthErrorCallback = (currentPath?: string): void => {
  const path = currentPath || window.location.pathname;
  authLogger.info('Calling auth error callback for path', { path });
  if (authErrorCallback) {
    authErrorCallback(path);
  }
};

// JWT Token interface
interface JWTTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: AuthUser;
  csrf_token?: string;
}

// Login Response interface to match backend LoginResponse
interface LoginResponse {
  success: boolean;
  message: string;
  requires_verification: boolean;
  requires_mfa?: boolean;
  mfa_token?: string;
  redirect_to?: string;
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  user?: AuthUser;
  csrf_token?: string;
}

export interface AuthUser {
  id: string;
  email: string;
  email_confirmed_at?: string | null;
  created_at?: string;
  updated_at?: string;
  user_metadata?: {
    email?: string;
    email_verified?: boolean;
    phone_verified?: boolean;
  };
  app_metadata?: {
    provider?: string;
    providers?: string[];
  };
}

export interface AuthSession {
  access_token: string;
  expires_at?: number;
  expires_in?: number;
  refresh_token?: string;
  token_type?: string;
  user?: AuthUser;
}

/**
 * Company SSO provider information returned by the backend
 */
export interface CompanySSOProviderInfo {
  company_identifier: string;
  provider_type: 'okta' | 'microsoft'; // Supported SSO providers
  domain: string;
  client_id: string;
  auth_server?: string; // For Okta (auth server) or Microsoft (tenant_id)
  redirect_uri?: string;
}

/**
 * Request data for Okta authentication
 */
export interface OktaAuthRequestData {
  id_token: string;
  company_identifier?: string;
}

export interface UserInfo {
  user: AuthUser | null;
  session: AuthSession | null;
}

export interface AuthServiceInterface {
  login(email: string, password: string): Promise<UserInfo>;
  verifyMFALogin(mfaToken: string, code: string, backupCode?: string): Promise<UserInfo>;
  register(
    email: string,
    password?: string,
    verificationCode?: string,
    firstName?: string,
    lastName?: string,
    invitationToken?: string,
    termsAccepted?: boolean,
    marketingEmailsOptIn?: boolean,
  ): Promise<UserInfo>;
  loginWithGoogle(verificationCode?: string): Promise<UserInfo>;
  loginWithMicrosoft(verificationCode?: string): Promise<UserInfo>;
  loginWithOkta(): Promise<void>;
  logout(): Promise<void>;
  getCurrentUser(): Promise<UserInfo>;
  refreshSession(): Promise<UserInfo>;
  syncWithBackend(): Promise<void>;
  getBackendUserId(): string | null;
  forgotPassword(email: string): Promise<void>;
  verifyResetPasswordCode(oobCode: string): Promise<{ email: string }>;
  confirmResetPassword(oobCode: string, newPassword: string): Promise<void>;
  isAuthenticated(): boolean;
  setAuthErrorCallback(callback: AuthErrorCallback): void;
  get isRecentlyAuthenticated(): boolean;
  get lastAuthTime(): number;
}

// Use the centralized API client
const apiClient = centralizedApiClient;

// Custom error classes for better type safety
export class EmailVerificationError extends Error {
  public readonly status: number;
  public readonly requiresVerification: boolean;
  public readonly redirectTo?: string;
  public readonly userEmail?: string;

  constructor(message: string, status: number = 403, redirectTo?: string, userEmail?: string) {
    super(message);
    this.name = 'EmailVerificationError';
    this.status = status;
    this.requiresVerification = true;
    this.redirectTo = redirectTo;
    this.userEmail = userEmail;
  }
}

export class MFAVerificationError extends Error {
  public readonly status: number;
  public readonly requiresMFA: boolean;
  public readonly userEmail?: string;
  public readonly user?: AuthUser;
  public readonly mfaToken?: string;

  constructor(
    message: string,
    status: number = 200,
    userEmail?: string,
    user?: AuthUser,
    mfaToken?: string,
  ) {
    super(message);
    this.name = 'MFAVerificationError';
    this.status = status;
    this.requiresMFA = true;
    this.userEmail = userEmail;
    this.user = user;
    this.mfaToken = mfaToken;
  }
}

class CentralizedAuthService implements AuthServiceInterface {
  private authProvider: AuthProvider;
  private _user: AuthUser | null = null;
  private _session: AuthSession | null = null;
  private _accessToken: string | null = null;
  private cachedGoogleToken: string | null = null;
  private cachedMicrosoftToken: string | null = null;
  private recentlyAuthenticated: boolean = false;
  private _lastAuthTime: number = 0;
  private _csrfToken: string | null = null;

  /**
   * Get the current access token from memory
   */
  public getAccessToken(): string | null {
    return this._accessToken;
  }

  /**
   * Get the current CSRF token from memory
   */
  public getCsrfToken(): string | null {
    return this._csrfToken;
  }

  constructor() {
    this.authProvider = authProvider;
    this.loadPersistedAuthState();

    // Register callbacks with the registry to avoid circular dependencies
    registerTokenRefreshCallback(() => this.refreshAccessTokenInternal());
    registerAuthErrorCallback((currentPath) => this.handleAuthError(currentPath));
    registerCsrfTokenGetter(() => this.getCsrfToken());
    registerAccessTokenGetter(() => this.getAccessToken());
    registerAccessTokenFetcher(() => this.fetchAccessTokenFromBackend());
  }

  /**
   * Fetch access token from backend for cross-origin requests
   * The token is extracted from HTTP-only cookies by the backend
   */
  public async fetchAccessTokenFromBackend(): Promise<string | null> {
    try {
      const response = await apiClient.get('/api/v1/auth/token', AUTH_HTTP_OPTIONS);
      const token = response.data?.access_token;
      if (token) {
        this._accessToken = token;
        authLogger.debug('Access token fetched from backend bridge and cached');
        return token;
      }
      return null;
    } catch (error) {
      authLogger.warn('Failed to fetch access token from backend bridge', { error });
      return null;
    }
  }

  /**
   * Get whether the user recently authenticated
   */
  get isRecentlyAuthenticated(): boolean {
    return this.recentlyAuthenticated;
  }

  /**
   * Get the timestamp of the last authentication
   */
  get lastAuthTime(): number {
    return this._lastAuthTime;
  }

  /**
   * Get the current user
   */
  get user(): AuthUser | null {
    return this._user;
  }

  /**
   * Get the current session
   */
  get session(): AuthSession | null {
    return this._session;
  }

  /**
   * Set callback function to be called when authentication errors occur
   */
  setAuthErrorCallback(callback: AuthErrorCallback): void {
    setAuthErrorCallback(callback);
  }

  /**
   * Handle auth errors internally
   */
  private handleAuthError(currentPath?: string): void {
    const path = currentPath || window.location.pathname;

    // Don't trigger auth error callback during email verification or if signup is in progress
    if (shouldSuppressRedirect(path)) {
      authLogger.debug('Skipping auth error handling during email verification', { path });
      return;
    }

    authLogger.info('Handling auth error for path', { path });
    if (authErrorCallback) {
      authErrorCallback(path);
    }
  }

  /**
   * Clear auth state.
   * Tokens are in HTTP-only cookies and cleared by backend logout.
   * @deprecated Cookies are cleared by the backend on logout
   */
  private clearTokens(): void {
    authLogger.info('Auth memory state cleared (cookies cleared by backend)');
  }

  /**
   * Load any startup auth state that is not persisted in localStorage.
   * Note: user/session are validated through backend (`/auth/me`) and React Query.
   */
  private loadPersistedAuthState(): void {
    // First, try to load CSRF token from cookie if not already set
    // This ensures we have a token even if no /auth/me call has been made yet
    if (!this._csrfToken) {
      this._loadCsrfTokenFromCookie();
    }
  }

  /**
   * Synchronize CSRF token from response if provided
   */
  private _syncCsrfToken(token: string | undefined, context: string): void {
    if (token) {
      this._csrfToken = token;
      authLogger.debug(`CSRF token synchronized: ${context}`);
    }
  }

  /**
   * Persist authentication state in memory.
   * User/session truth is server-backed and fetched via `/auth/me`.
   */
  private persistAuthState(
    user: AuthUser | null,
    session: AuthSession | null,
    csrf_token?: string,
    access_token?: string,
  ): void {
    if (user && session) {
      // Store tokens in memory for direct API calls (e.g. streaming)
      if (access_token) {
        this._accessToken = access_token;
      } else if (session.access_token) {
        this._accessToken = session.access_token;
      }

      // Store CSRF token in memory if provided
      this._syncCsrfToken(csrf_token, 'persist state');

      this._user = user;
      this._session = session;
      authLogger.debug('Auth state persisted in memory');
    } else {
      this.clearTokens();
      this._accessToken = null;
      authLogger.debug('Auth state cleared');
    }
  }

  /**
   * Cleanup authentication state
   */
  private cleanup(): void {
    this._user = null;
    this._session = null;
    this.recentlyAuthenticated = false;
    this._lastAuthTime = 0;
    this.clearTokens();

    // Clear additional authentication-related storage
    localStorage.removeItem('auth_session');
    localStorage.removeItem('csrf_token');

    sessionStorage.removeItem('new_user_signup');

    notifyAuthStateChange('logged_out');
    authLogger.info('Authentication state fully cleaned up');
  }

  /**
   * Handle repeated 500 errors during token refresh (indicates corrupted tokens)
   */
  private handleCorruptedTokens(): void {
    const failures = Number.parseInt(localStorage.getItem(REFRESH_FAILURE_KEY) || '0', 10);
    const newFailures = failures + 1;

    localStorage.setItem(REFRESH_FAILURE_KEY, newFailures.toString());

    // After MAX_REFRESH_FAILURES consecutive 500 errors, assume tokens are corrupted and force logout
    if (newFailures >= MAX_REFRESH_FAILURES) {
      authLogger.error(
        'Maximum token refresh failures reached, forcing logout to clear corrupted tokens',
      );
      localStorage.removeItem(REFRESH_FAILURE_KEY); // Reset counter

      // Force logout to clear corrupted tokens
      this.cleanup();
      triggerAuthError();
      return;
    }

    authLogger.warn(`Token refresh failure count: ${newFailures}/${MAX_REFRESH_FAILURES}`);
  }

  /**
   * Reset the corrupted token counter (called on successful operations)
   */
  private resetCorruptedTokenCounter(): void {
    if (localStorage.getItem(REFRESH_FAILURE_KEY)) {
      localStorage.removeItem(REFRESH_FAILURE_KEY);
      authLogger.info('Reset corrupted token failure counter after successful operation');
    }
  }

  /**
   * Internal refresh access token implementation
   * Note: Refresh token is in HTTP-only cookie, sent automatically with request
   */
  private async refreshAccessTokenInternal(): Promise<void> {
    const startTime = performance.now();
    const currentUserId = this._user?.id;

    try {
      authLogger.info(
        'Attempting to refresh access token (using refresh token from HTTP-only cookie)',
      );
      // Refresh token is in HTTP-only cookie, sent automatically with credentials: 'include'
      // No need to check for refresh token - it's in the cookie
      const response = await apiClient.post('/api/v1/auth/refresh', {}, AUTH_HTTP_OPTIONS);

      const { access_token, token_type, expires_in } = response.data;

      if (!access_token) {
        const errorMsg = 'No access token received from refresh endpoint';

        // Track token refresh failure
        securityEventTracker.trackEvent({
          type: 'api_error',
          severity: 'high',
          success: false,
          errorMessage: errorMsg,
          metadata: {
            operation: 'token_refresh',
            userId: currentUserId,
            responseData: response.data,
          },
        });

        throw new Error(errorMsg);
      }

      // Tokens are now in HTTP-only cookies, not accessible via JavaScript
      // Update session object (tokens are in cookies)
      if (this._session) {
        this._session.token_type = token_type;
        this._session.expires_in = expires_in;
        // access_token is not accessible from JavaScript (in HTTP-only cookie)
      }

      // Persist user info (tokens are in cookies)
      this.persistAuthState(this._user, this._session, response.data.csrf_token, access_token);

      // Reset failure counter on successful refresh
      this.resetCorruptedTokenCounter();

      // Track successful token refresh
      securityEventTracker.trackEvent({
        type: 'login_success', // Token refresh is essentially re-authentication
        severity: 'low',
        success: true,
        userId: currentUserId,
        metadata: {
          operation: 'token_refresh',
          tokenType: token_type,
          expiresIn: expires_in,
          tokenLength: access_token.length,
        },
        performanceMetrics: {
          duration: performance.now() - startTime,
          timestamp: performance.now(),
        },
      });

      authLogger.info('Access token refreshed and persisted successfully', {
        tokenLength: access_token.length,
        tokenType: token_type,
        expiresIn: expires_in,
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown token refresh error';

      // Track token refresh failure
      securityEventTracker.trackEvent({
        type: 'api_error',
        severity: 'medium',
        success: false,
        errorMessage,
        userId: currentUserId,
        metadata: {
          operation: 'token_refresh',
          errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
        },
        performanceMetrics: {
          duration: performance.now() - startTime,
          timestamp: performance.now(),
        },
      });

      authLogger.error('Failed to refresh access token', error);

      // Handle different error types appropriately
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        const status = axiosError.response?.status;

        // Track specific error types for security monitoring
        if (status === 401 || status === 403) {
          securityEventTracker.trackEvent({
            type: 'suspicious_activity',
            severity: 'medium',
            success: false,
            errorMessage: `Token refresh failed with ${status}`,
            userId: currentUserId,
            metadata: {
              operation: 'token_refresh',
              httpStatus: status,
              possibleTokenTheft: true,
            },
          });
        }

        if (status === 500) {
          authLogger.warn('Server error during token refresh, may indicate corrupted tokens');
          this.handleCorruptedTokens();
        }
        // For 401/400 errors: refresh token is invalid/expired (cookie will be cleared by backend)
        else if (status === 401 || status === 400) {
          authLogger.warn('Refresh token is invalid or expired (cookie cleared by backend)');
        }
        // For temporary server errors: keep refresh token for retry
        else if ([502, 503, 504].includes(status)) {
          authLogger.warn(
            `Temporary backend error (${status}) during token refresh, retaining refresh token`,
          );
          // Don't clear tokens for temporary backend issues
        }
        // For other HTTP errors: keep refresh token
        else {
          authLogger.warn(`HTTP ${status} error during token refresh, retaining refresh token`);
        }
      } else {
        // For network errors or other issues, keep refresh token for retry
        authLogger.warn(
          'Network or other error during token refresh, keeping refresh token for retry',
        );
      }

      if (error instanceof Error) {
        throw new AuthError('Token refresh failed', error);
      } else {
        throw new AuthError('Token refresh failed');
      }
    }
  }

  /**
   * Checks if email is verified from login response
   */
  private checkEmailVerificationStatus(loginResponse: LoginResponse): {
    isEmailVerified: boolean;
    hasTokens: boolean;
  } {
    const isEmailVerified =
      loginResponse.user?.email_confirmed_at != null ||
      loginResponse.user?.user_metadata?.email_verified === true;
    const hasTokens = !!(loginResponse.success && loginResponse.access_token);
    return { isEmailVerified, hasTokens };
  }

  /**
   * Handles email verification required case
   */
  private handleEmailVerificationRequired(
    loginResponse: LoginResponse,
    email: string,
    isEmailVerified: boolean,
    hasTokens: boolean,
  ): void {
    if (!loginResponse.requires_verification || isEmailVerified || hasTokens) {
      if (loginResponse.requires_verification && (isEmailVerified || hasTokens)) {
        authLogger.info('Email already verified or tokens present, proceeding with login', {
          email,
          hasTokens,
          isEmailVerified,
          email_confirmed_at: loginResponse.user?.email_confirmed_at,
          user_metadata: loginResponse.user?.user_metadata,
        });
      }
      return;
    }

    authLogger.info('Email verification required - no tokens and email not verified', {
      email,
      hasTokens,
      isEmailVerified,
      hasUser: !!loginResponse.user,
    });

    securityEventTracker.trackAuthEvent(
      'login_failure',
      false,
      email,
      'Email verification required',
      {
        method: 'email_password',
        requiresVerification: true,
        redirectTo: loginResponse.redirect_to,
      },
    );

    const error = new EmailVerificationError(
      loginResponse.message || 'Email verification required',
      loginResponse.success ? 200 : 403,
      loginResponse.redirect_to,
      loginResponse.user?.email || email,
    );
    throw error;
  }

  /**
   * Validates login response has all required fields
   */
  private validateLoginResponse(loginResponse: LoginResponse, email: string): void {
    if (
      loginResponse.refresh_token &&
      loginResponse.token_type &&
      loginResponse.expires_in &&
      loginResponse.user
    ) {
      return;
    }

    const errorMsg = 'Incomplete login response: Missing required fields';
    securityEventTracker.trackAuthEvent('login_failure', false, email, errorMsg, {
      method: 'email_password',
      missingFields: {
        refresh_token: !loginResponse.refresh_token,
        token_type: !loginResponse.token_type,
        expires_in: !loginResponse.expires_in,
        user: !loginResponse.user,
      },
    });

    throw new Error(errorMsg);
  }

  /**
   * Creates tokens and session from login response
   */
  private createTokensFromResponse(loginResponse: LoginResponse): JWTTokens {
    return {
      access_token: loginResponse.access_token!,
      refresh_token: loginResponse.refresh_token!,
      token_type: loginResponse.token_type!,
      expires_in: loginResponse.expires_in!,
      user: loginResponse.user!,
      csrf_token: loginResponse.csrf_token,
    };
  }

  /**
   * Handles successful login flow
   */
  private async handleSuccessfulLogin(
    tokens: JWTTokens,
    email: string,
    startTime: number,
  ): Promise<UserInfo> {
    // Use createUserSessionFromTokens as single source of truth for provider metadata
    // This ensures app_metadata is set correctly for email/password users
    const userInfo = this.createUserSessionFromTokens(tokens, 'email');

    await this.syncWithBackend();

    securityEventTracker.trackAuthEvent('login_success', true, email, undefined, {
      method: 'email_password',
      userId: tokens.user.id,
      tokenType: tokens.token_type,
      expiresIn: tokens.expires_in,
    });

    securityEventTracker.trackPerformance('login', startTime, true, {
      method: 'email_password',
      userId: tokens.user.id,
    });

    return userInfo;
  }

  /**
   * Handles login errors
   */
  private handleLoginError(error: unknown, email: string, startTime: number): never {
    const errorMessage = error instanceof Error ? error.message : 'Unknown login error';

    if (!(error instanceof EmailVerificationError)) {
      securityEventTracker.trackAuthEvent('login_failure', false, email, errorMessage, {
        method: 'email_password',
        errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
      });

      securityEventTracker.trackPerformance('login', startTime, false, {
        method: 'email_password',
        error: errorMessage,
      });
    }

    throw handleError(error);
  }

  async login(email: string, password: string): Promise<UserInfo> {
    const startTime = performance.now();

    securityEventTracker.trackAuthEvent('login_attempt', true, email, undefined, {
      method: 'email_password',
    });

    try {
      const response = await apiClient.post(
        '/api/v1/auth/login',
        {
          email,
          password,
        },
        AUTH_HTTP_OPTIONS,
      );

      const loginResponse: LoginResponse = response.data;

      // Check if MFA is required
      if (loginResponse.requires_mfa && loginResponse.mfa_token) {
        authLogger.info('MFA verification required for login', { email });
        throw new MFAVerificationError(
          loginResponse.message || 'MFA verification required',
          200,
          email,
          loginResponse.user,
          loginResponse.mfa_token,
        );
      }

      const { isEmailVerified, hasTokens } = this.checkEmailVerificationStatus(loginResponse);

      this.handleEmailVerificationRequired(loginResponse, email, isEmailVerified, hasTokens);

      if (!loginResponse.success || !loginResponse.access_token) {
        const errorMsg = loginResponse.message || 'Login failed';
        securityEventTracker.trackAuthEvent('login_failure', false, email, errorMsg, {
          method: 'email_password',
        });
        throw new Error(errorMsg);
      }

      this.validateLoginResponse(loginResponse, email);
      const tokens = this.createTokensFromResponse(loginResponse);
      return await this.handleSuccessfulLogin(tokens, email, startTime);
    } catch (error: unknown) {
      // Preserve MFA error properties before error handling
      if (error && typeof error === 'object' && 'requires_mfa' in error) {
        // Don't transform MFA errors - let them pass through
        throw error;
      }
      this.handleLoginError(error, email, startTime);
    }
  }

  /**
   * Verifies MFA login with the provided token and verification code
   * @param mfaToken - The MFA token received from the initial login attempt
   * @param code - The MFA verification code from the authenticator app
   * @param backupCode - Optional backup code for MFA verification
   * @returns Promise resolving to user information on successful verification
   */
  async verifyMFALogin(mfaToken: string, code: string, backupCode?: string): Promise<UserInfo> {
    const startTime = performance.now();

    securityEventTracker.trackMFAEvent('mfa_verify_attempt', true, undefined, {
      method: 'email_password',
    });

    try {
      const response = await apiClient.post(
        '/api/v1/auth/login/verify-mfa',
        {
          mfa_token: mfaToken,
          code: code || undefined,
          backup_code: backupCode || undefined,
        },
        AUTH_HTTP_OPTIONS,
      );

      const loginResponse: LoginResponse = response.data;

      if (!loginResponse.success || !loginResponse.access_token) {
        const errorMsg = loginResponse.message || 'MFA verification failed';
        const userEmail = loginResponse.user?.email || 'user';
        securityEventTracker.trackMFAEvent('mfa_verify_failure', false, errorMsg, {
          method: 'email_password',
          email: userEmail,
        });
        throw new Error(errorMsg);
      }

      const userEmail = loginResponse.user?.email || 'user';
      this.validateLoginResponse(loginResponse, userEmail);
      const tokens = this.createTokensFromResponse(loginResponse);

      // Use createUserSessionFromTokens as single source of truth for provider metadata
      const userInfo = this.createUserSessionFromTokens(tokens, 'email');

      // Sync with backend and track security events
      await this.syncWithBackend();

      securityEventTracker.trackMFAEvent('mfa_verify_success', true, undefined, {
        method: 'email_password',
        email: userEmail,
      });

      securityEventTracker.trackPerformance('mfa_verify', startTime, true, {
        method: 'email_password',
        userId: tokens.user?.id,
      });

      return userInfo;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      securityEventTracker.trackMFAEvent('mfa_verify_failure', false, errorMessage, {
        method: 'email_password',
        error: errorMessage,
      });
      this.handleLoginError(error, 'user', startTime);
    }
  }

  /**
   * Resend MFA verification code to the user's email.
   * @param mfaToken - The MFA token from the initial login (requires_mfa response)
   * @returns Promise with success, message, and optional retry_after (seconds)
   */
  async resendMFACode(
    mfaToken: string,
  ): Promise<{ success: boolean; message: string; retry_after?: number }> {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      retry_after?: number;
    }>('/api/v1/auth/login/resend-mfa-code', { mfa_token: mfaToken });
    return response.data;
  }

  async register(
    email: string,
    password?: string,
    verificationCode?: string,
    firstName?: string,
    lastName?: string,
    invitationToken?: string,
    termsAccepted?: boolean,
    marketingEmailsOptIn?: boolean,
  ): Promise<UserInfo> {
    try {
      const payload = {
        email,
        password,
        verification_code: verificationCode,
        first_name: firstName,
        last_name: lastName,
        invitation_token: invitationToken,
        terms_accepted: termsAccepted,
        marketing_emails_opt_in: marketingEmailsOptIn,
      };

      // Remove undefined values from payload
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v !== undefined),
      );

      const response = await apiClient.post(
        '/api/v1/auth/register',
        filteredPayload,
        AUTH_HTTP_OPTIONS,
      );

      const loginResponse: LoginResponse = response.data;
      const tokens = this.createTokensFromResponse(loginResponse);

      // Use createUserSessionFromTokens as single source of truth for provider metadata
      // This ensures app_metadata is set correctly for email/password users
      const userInfo = this.createUserSessionFromTokens(tokens, 'email');

      await this.syncWithBackend();

      return userInfo;
    } catch (error: unknown) {
      throw handleError(error);
    }
  }

  /**
   * Gets the Google ID token, either from cache or by signing in
   */
  private async getGoogleIdToken(verificationCode?: string): Promise<string> {
    return this.getOAuthIdToken('google', verificationCode);
  }

  /**
   * Gets the Microsoft ID token by signing in
   * @param verificationCode - Optional verification code for new user registration
   * @returns Promise<string> - The ID token
   */
  private async getMicrosoftIdToken(verificationCode?: string): Promise<string> {
    return this.getOAuthIdToken('microsoft', verificationCode);
  }

  /**
   * Returns cached token and clears it when verification reuse is allowed; otherwise null.
   */
  private tryReuseCachedOAuthToken(
    provider: 'google' | 'microsoft',
    verificationCode: string | undefined,
  ): string | null {
    const cachedToken = provider === 'google' ? this.cachedGoogleToken : this.cachedMicrosoftToken;
    if (!verificationCode || !cachedToken) {
      return null;
    }
    authLogger.info(`Reusing cached ${provider} token with verification code`);
    if (provider === 'google') {
      this.cachedGoogleToken = null;
    } else {
      this.cachedMicrosoftToken = null;
    }
    return cachedToken;
  }

  private async invokeOAuthProviderSignIn(
    oauthProvider: OAuthAuthProvider,
    provider: 'google' | 'microsoft',
  ): Promise<AuthResponse> {
    return provider === 'google'
      ? oauthProvider.signInWithGoogle()
      : oauthProvider.signInWithMicrosoft();
  }

  /**
   * Generic method to get OAuth ID token for Google or Microsoft providers
   * @param provider - The OAuth provider ('google' | 'microsoft')
   * @param verificationCode - Optional verification code for new user registration
   * @returns Promise<string> - The ID token
   */
  private async getOAuthIdToken(
    provider: 'google' | 'microsoft',
    verificationCode?: string,
  ): Promise<string> {
    const fromCache = this.tryReuseCachedOAuthToken(provider, verificationCode);
    if (fromCache !== null) {
      return fromCache;
    }

    if (!supportsOAuth(this.authProvider)) {
      throw new AuthError('Auth provider does not support OAuth');
    }

    const oauthProvider = this.authProvider as OAuthAuthProvider;
    const signInResult = await this.invokeOAuthProviderSignIn(oauthProvider, provider);

    if (signInResult.error) {
      throw new AuthError(
        buildOAuthSignInFailureMessage(provider, signInResult.error),
        signInResult.error,
      );
    }

    const { session } = signInResult.data;
    if (!session) {
      throw new AuthError(
        provider === 'google'
          ? 'Google sign-in could not be completed. Please try again.'
          : 'Microsoft sign-in could not be completed. Please try again.',
      );
    }

    return session.access_token;
  }

  /**
   * Builds the payload for the unified API call
   */
  private buildUnifiedPayload(
    idToken: string,
    verificationCode?: string,
  ): {
    id_token: string;
    verification_code?: string;
  } {
    const payload: { id_token: string; verification_code?: string } = {
      id_token: idToken,
    };

    if (verificationCode) {
      payload.verification_code = verificationCode;
    }

    return payload;
  }

  /**
   * Handles API error and caches token if verification code is required
   */
  private handleUnifiedApiError(
    apiError: unknown,
    idToken: string,
    verificationCode?: string,
    provider: 'google' | 'microsoft' = 'google',
  ): never {
    authLogger.error('Unified API call failed', { error: apiError });

    if (!verificationCode && apiError && typeof apiError === 'object' && 'response' in apiError) {
      const axiosError = apiError as any;
      const errorDetail = axiosError.response?.data?.detail || '';
      if (
        errorDetail.includes('Verification code is required') &&
        errorDetail.includes('new account registration')
      ) {
        if (provider === 'microsoft') {
          authLogger.info('Caching Microsoft token for verification code retry');
          this.cachedMicrosoftToken = idToken;
        } else {
          authLogger.info('Caching Google token for verification code retry');
          this.cachedGoogleToken = idToken;
        }
      }
    }

    throw apiError;
  }

  /**
   * Helper method to make unified API call for OAuth providers
   * Consolidates duplicated code between loginWithGoogle and loginWithMicrosoft
   */
  private async makeUnifiedApiCall(
    idToken: string,
    verificationCode: string | undefined,
    provider: 'google' | 'microsoft',
  ): Promise<UserInfo> {
    const payload = this.buildUnifiedPayload(idToken, verificationCode);

    authLogger.info(`Making unified API call for ${provider}`, {
      payload: { ...payload, id_token: payload.id_token ? '[REDACTED]' : undefined },
    });

    try {
      const response = await apiClient.post('/api/v1/auth/unified', payload, AUTH_HTTP_OPTIONS);
      authLogger.info('Unified API call successful');
      const loginResponse: LoginResponse = response.data;
      const tokens = this.createTokensFromResponse(loginResponse);
      return this.createUserSessionFromTokens(tokens, provider);
    } catch (apiError: unknown) {
      this.handleUnifiedApiError(apiError, idToken, verificationCode, provider);
    }
  }

  /**
   * Creates user and session from tokens with provider metadata
   * Note: Tokens are now in HTTP-only cookies, so we only store user info
   * @param authMethod - Optional: 'google' for Google OAuth, 'microsoft' for Microsoft OAuth, 'email' for email/password, undefined to preserve backend metadata
   */
  private createUserSessionFromTokens(
    tokens: JWTTokens,
    authMethod?: 'google' | 'microsoft' | 'email' | 'okta',
  ): UserInfo {
    // Preserve app_metadata from backend if it exists
    let app_metadata = tokens.user?.app_metadata;

    // Only set app_metadata if:
    // 1. Backend didn't provide it AND
    // 2. We know the auth method used
    if (!app_metadata && authMethod) {
      if (authMethod === 'google') {
        app_metadata = {
          provider: 'google_identity_platform',
          providers: ['google_identity_platform'],
        };
      } else if (authMethod === 'microsoft') {
        app_metadata = {
          provider: 'microsoft',
          providers: ['microsoft'],
        };
      } else if (authMethod === 'email') {
        // For email/password users, don't set app_metadata (or set to email)
        // This ensures isEmailAuthenticatedUser returns true
        app_metadata = {
          provider: 'email',
          providers: ['email'],
        };
      } else if (authMethod === 'okta') {
        app_metadata = {
          provider: 'okta',
          providers: ['okta'],
        };
      }
    }

    // If backend provided app_metadata, use it (don't overwrite)
    // If we set it above, use that
    // Otherwise, leave it undefined (which makes isEmailAuthenticatedUser return true)
    this._user = {
      ...tokens.user,
      ...(app_metadata && { app_metadata }),
    };

    // Tokens are in HTTP-only cookies, not accessible via JavaScript
    this._session = {
      access_token: '', // Not accessible from JavaScript
      refresh_token: '', // Not accessible from JavaScript
      token_type: tokens.token_type,
      expires_in: tokens.expires_in,
      user: this._user,
    };

    // Mark as recently authenticated
    this.recentlyAuthenticated = true;
    this._lastAuthTime = Date.now();

    this.persistAuthState(this._user, this._session, tokens.csrf_token, tokens.access_token);

    return { user: this._user, session: this._session };
  }

  async loginWithGoogle(verificationCode?: string): Promise<UserInfo> {
    if (!supportsOAuth(this.authProvider)) {
      throw new AuthError('Google OAuth not supported by current auth provider');
    }

    const idToken = await this.getGoogleIdToken(verificationCode);
    return this.makeUnifiedApiCall(idToken, verificationCode, 'google');
  }

  async loginWithMicrosoft(verificationCode?: string): Promise<UserInfo> {
    if (!supportsOAuth(this.authProvider)) {
      throw new AuthError('Microsoft OAuth not supported by current auth provider');
    }

    const idToken = await this.getMicrosoftIdToken(verificationCode);
    return this.makeUnifiedApiCall(idToken, verificationCode, 'microsoft');
  }

  /**
   * Send password reset email
   * @param email - User's email address
   */
  async forgotPassword(email: string): Promise<void> {
    try {
      await apiClient.post('/api/v1/auth/forgot-password', { email }, AUTH_HTTP_OPTIONS);
    } catch (error) {
      throw handleError(error);
    }
  }

  /**
   * Verify password reset code
   * @param oobCode - Firebase OOB code for password reset
   */
  async verifyResetPasswordCode(oobCode: string): Promise<{ email: string }> {
    try {
      const response = await apiClient.get(
        `/api/v1/auth/reset-password/verify?oobCode=${encodeURIComponent(oobCode)}`,
      );
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  }

  /**
   * Confirm password reset and set new password
   * @param oobCode - Firebase OOB code for password reset
   * @param newPassword - The new password
   */
  async confirmResetPassword(oobCode: string, newPassword: string): Promise<void> {
    try {
      await apiClient.post(
        '/api/v1/auth/reset-password',
        {
          oobCode,
          newPassword,
        },
        AUTH_HTTP_OPTIONS,
      );
    } catch (error) {
      throw handleError(error);
    }
  }

  async getCompanySSOProvider(companyIdentifier: string): Promise<CompanySSOProviderInfo | null> {
    try {
      const response = await centralizedApiClient.get<CompanySSOProviderInfo>(
        `/api/v1/auth/company-sso/${companyIdentifier}`,
      );
      return response.data;
    } catch (error: any) {
      authLogger.error('Failed to get SSO provider', {
        companyIdentifier,
        error: error.message,
      });
      return null;
    }
  }

  /**
   * High-level method to login with company SSO.
   * Handles provider lookup, validation, configuration persistence, and redirection.
   * @param companyIdentifier - The company identifier (e.g., karbondigital)
   */
  async loginWithCompanySSO(companyIdentifier: string): Promise<void> {
    authLogger.info('High-level company SSO login initiated', { companyIdentifier });

    // Get SSO provider info for this company
    const providerInfo = await this.getCompanySSOProvider(companyIdentifier);

    if (!providerInfo) {
      throw new AuthError(
        'No SSO configuration found for this company. Please contact your administrator.',
      );
    }

    authLogger.info('Found SSO provider for company', {
      companyIdentifier,
      providerType: providerInfo.provider_type,
    });

    // Store the provider info for the callback (centralized here to avoid duplication in UI)
    sessionStorage.setItem('sso_provider_info', JSON.stringify(providerInfo));

    // Route to appropriate SSO provider
    if (providerInfo.provider_type === 'okta') {
      await this.loginWithCompanyOkta(providerInfo);
    } else if (providerInfo.provider_type === 'microsoft') {
      await this.loginWithCompanyMicrosoft(providerInfo);
    } else {
      throw new AuthError(`SSO provider ${providerInfo.provider_type} is not yet supported.`);
    }
  }

  async loginWithOkta(): Promise<void> {
    authLogger.info('Initiating Okta SSO login');

    // Get Okta configuration from environment
    const oktaDomain = ENV.VITE_OKTA_DOMAIN;
    const oktaClientId = ENV.VITE_OKTA_CLIENT_ID;
    // Authorization server path (defaults to 'default' for Okta, can be customized)
    const oktaAuthServer = ENV.VITE_OKTA_AUTH_SERVER || 'default';
    // Redirect URI - can be configured via env var, otherwise defaults to /auth/okta/callback
    const redirectUri =
      ENV.VITE_OKTA_REDIRECT_URI || `${window.location.origin}/auth/okta/callback`;

    try {
      // Validate required environment variables
      if (!oktaDomain || oktaDomain.trim() === '') {
        throw new AuthError(
          'Okta domain is not configured. Please set VITE_OKTA_DOMAIN in your environment variables.',
        );
      }

      if (!oktaClientId || oktaClientId.trim() === '') {
        throw new AuthError(
          'Okta client ID is not configured. Please set VITE_OKTA_CLIENT_ID in your environment variables.',
        );
      }

      // Build Okta authorization URL
      // Support both Okta format: /oauth2/{server} and Auth0 format: /authorize
      const isAuth0Domain = oktaDomain.includes('auth0.com');
      const oktaIssuer = isAuth0Domain
        ? `https://${oktaDomain}`
        : `https://${oktaDomain}/oauth2/${oktaAuthServer}`;
      const authPath = isAuth0Domain ? '/authorize' : '/v1/authorize';
      const authUrl = new URL(`${oktaIssuer}${authPath}`);

      const state = this.generateState();
      const verifier = await this.generateCodeVerifier();
      const challenge = await this.generateCodeChallenge(verifier);

      authUrl.searchParams.set('client_id', oktaClientId);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', 'openid profile email');
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('code_challenge', challenge);
      authUrl.searchParams.set('code_challenge_method', 'S256');

      // Store state and code verifier for verification
      sessionStorage.setItem('okta_state', state);
      sessionStorage.setItem('okta_verifier', verifier);

      const finalUrl = authUrl.toString();

      // Redirect to Okta for authentication
      window.location.href = finalUrl;

      // VERY IMPORTANT: Do NOT throw error here - it causes auth state reset
      // The redirect will happen and user will authenticate on Okta
      // Since we redirect, this promise will never resolve, but we return it for type consistency
      return;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorDetails = {
        error: errorMessage,
        oktaDomain: !!oktaDomain,
        oktaClientId: !!oktaClientId,
        oktaAuthServer,
        redirectUri,
      };
      authLogger.error('Okta login initiation failed', errorDetails);
      throw error;
    }
  }

  async loginWithCompanyOkta(providerInfo: CompanySSOProviderInfo): Promise<void> {
    authLogger.info('Initiating company Okta SSO login', {
      companyIdentifier: providerInfo.company_identifier,
      domain: providerInfo.domain,
    });

    try {
      // Use provider info instead of environment variables
      const oktaDomain = providerInfo.domain;
      const oktaClientId = providerInfo.client_id;
      const oktaAuthServer = providerInfo.auth_server || 'default';
      // Use database redirect_uri if available, otherwise construct from current domain
      let redirectUri = providerInfo.redirect_uri;
      if (!redirectUri) {
        // Construct redirect URI from current domain
        const currentOrigin = window.location.origin;
        redirectUri = `${currentOrigin}/auth/okta/callback`;
      } else {
        // If database has a redirect_uri, use it as-is (allows custom domains per company)
        // This allows companies to have their own branded domains
      }

      // Validate required provider info
      if (!oktaDomain || oktaDomain.trim() === '') {
        throw new AuthError('Okta domain is not configured for this company.');
      }

      if (!oktaClientId || oktaClientId.trim() === '') {
        throw new AuthError('Okta client ID is not configured for this company.');
      }

      // Build Okta authorization URL using provider info
      const isAuth0Domain = oktaDomain.includes('auth0.com');
      const oktaIssuer = isAuth0Domain
        ? `https://${oktaDomain}`
        : `https://${oktaDomain}/oauth2/${oktaAuthServer}`;
      const authPath = isAuth0Domain ? '/authorize' : '/v1/authorize';
      const authUrl = new URL(`${oktaIssuer}${authPath}`);

      const state = this.generateState();
      const verifier = await this.generateCodeVerifier();
      const challenge = await this.generateCodeChallenge(verifier);

      // Store verifier and state for callback (use same keys as callback component expects)
      sessionStorage.setItem('okta_verifier', verifier);
      sessionStorage.setItem('okta_state', state);

      // Build authorization URL parameters
      const params = new URLSearchParams({
        client_id: oktaClientId,
        response_type: 'code',
        scope: 'openid email profile',
        redirect_uri: redirectUri,
        state: state,
        code_challenge: challenge,
        code_challenge_method: 'S256',
      });

      authUrl.search = params.toString();

      authLogger.info('Redirecting to Okta authorization URL', {
        url: authUrl.toString(),
        domain: oktaDomain,
        clientId: oktaClientId.substring(0, 8) + '...',
      });

      // Redirect to Okta
      window.location.href = authUrl.toString();
    } catch (error: any) {
      const errorDetails = {
        message: error.message,
        oktaDomain: !!providerInfo?.domain,
        oktaClientId: !!providerInfo?.client_id,
        oktaAuthServer: providerInfo?.auth_server,
        redirectUri: providerInfo?.redirect_uri,
      };
      authLogger.error('Company Okta login initiation failed', errorDetails);
      throw error;
    }
  }

  /**
   * Initiates Microsoft Azure AD SSO login with company-specific configuration
   * Follows the same pattern as loginWithCompanyOkta for consistency
   */
  async loginWithCompanyMicrosoft(providerInfo: CompanySSOProviderInfo): Promise<void> {
    authLogger.info('Initiating company Microsoft Azure AD SSO login', {
      companyIdentifier: providerInfo.company_identifier,
      domain: providerInfo.domain,
    });

    try {
      // Use provider info from database
      // Note: auth_server contains the tenant_id for Microsoft
      const tenantId = providerInfo.auth_server || 'common';
      const clientId = providerInfo.client_id;
      // Use database redirect_uri if available, otherwise construct from current domain
      let redirectUri = providerInfo.redirect_uri;
      if (!redirectUri) {
        // Construct redirect URI from current domain
        const currentOrigin = window.location.origin;
        redirectUri = `${currentOrigin}/auth/microsoft/callback`;
      }

      // Validate required provider info
      if (!clientId || clientId.trim() === '') {
        throw new AuthError('Microsoft client ID is not configured for this company.');
      }

      // Build Microsoft authorization URL
      const authUrl = new URL(
        `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`,
      );

      const state = this.generateState();
      const verifier = await this.generateCodeVerifier();
      const challenge = await this.generateCodeChallenge(verifier);

      // Store state, verifier, and provider info for callback
      sessionStorage.setItem('microsoft_state', state);
      sessionStorage.setItem('microsoft_verifier', verifier);

      // Build authorization URL parameters following Microsoft OAuth 2.0 PKCE flow
      const params = new URLSearchParams({
        client_id: clientId,
        response_type: 'code',
        scope: 'openid email profile',
        redirect_uri: redirectUri,
        state: state,
        code_challenge: challenge,
        code_challenge_method: 'S256',
        prompt: 'select_account', // Allow user to select account
      });

      authUrl.search = params.toString();

      authLogger.info('Redirecting to Microsoft authorization URL', {
        url: authUrl.toString(),
        tenantId,
        clientId: clientId.substring(0, 8) + '...',
      });

      // Redirect to Microsoft
      window.location.href = authUrl.toString();
    } catch (error: any) {
      const errorDetails = {
        message: error.message,
        microsoftClientId: !!providerInfo?.client_id,
        microsoftAuthServer: providerInfo?.auth_server,
        redirectUri: providerInfo?.redirect_uri,
      };
      authLogger.error('Company Microsoft login initiation failed', errorDetails);
      throw error;
    }
  }

  private generateState(): string {
    // Generate cryptographically secure random state for CSRF protection
    const array = new Uint8Array(16); // 16 bytes = 128 bits of entropy
    crypto.getRandomValues(array);

    // Convert to base64url-safe string
    let base64 = btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_');

    // Remove trailing '=' padding safely (base64 always has 0 or 1 padding char for 16 bytes)
    while (base64.endsWith('=')) {
      base64 = base64.slice(0, -1);
    }

    return base64;
  }

  private async generateCodeVerifier(): Promise<string> {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    let base64 = btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_');

    // Remove trailing '=' padding safely (base64 always has 0, 1, or 2 padding chars)
    while (base64.endsWith('=')) {
      base64 = base64.slice(0, -1);
    }

    return base64;
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);

    let base64 = btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_');

    // Remove trailing '=' padding safely (SHA-256 digest always produces predictable base64)
    while (base64.endsWith('=')) {
      base64 = base64.slice(0, -1);
    }

    return base64;
  }

  /**
   * Generic SSO callback handler for any provider (Okta, Microsoft, etc.)
   * Eliminates code duplication between provider-specific handlers
   */
  private async handleSSOCallbackGeneric(
    code: string,
    provider: string,
    stateStorageKey: string,
    verifierStorageKey: string,
    tokenExchangeFn: (code: string, verifier: string) => Promise<any>,
    backendEndpoint: string,
    requestDataBuilder: (tokens: any) => any,
  ): Promise<UserInfo> {
    try {
      // Verify state (CSRF protection)
      const storedState = sessionStorage.getItem(stateStorageKey);
      if (!storedState) {
        throw new AuthError(`${provider} state not found. Please try logging in again.`);
      }
      sessionStorage.removeItem(stateStorageKey);

      // Get the code verifier from session storage
      const codeVerifier = sessionStorage.getItem(verifierStorageKey);
      if (!codeVerifier) {
        throw new AuthError('Code verifier not found. Please try logging in again.');
      }
      sessionStorage.removeItem(verifierStorageKey);

      // Exchange authorization code for tokens
      const tokens = await tokenExchangeFn(code, codeVerifier);

      // Send tokens to backend for session creation and verification
      authLogger.info(`Sending ${provider} tokens to backend for authentication`);

      // Check if this is company-based SSO by looking for stored provider info
      const storedProviderInfo = sessionStorage.getItem('sso_provider_info');
      let requestData = requestDataBuilder(tokens);

      if (storedProviderInfo) {
        try {
          const providerInfo = JSON.parse(storedProviderInfo);
          requestData.company_identifier = providerInfo.company_identifier;
          authLogger.info(`Including company identifier in ${provider} auth request`, {
            company_identifier: providerInfo.company_identifier,
          });
        } catch (e) {
          authLogger.warn('Failed to parse stored provider info', { error: e });
        }
      }

      const response = await apiClient.post(backendEndpoint, requestData, AUTH_HTTP_OPTIONS);

      authLogger.info('Backend response received', {
        status: response.status,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
      });

      authLogger.info('Backend authentication completed');
      const jwtTokens: JWTTokens = response.data;
      const userInfo = this.createUserSessionFromTokens(
        jwtTokens,
        provider as 'okta' | 'microsoft' | 'email' | 'google',
      );

      return userInfo;
    } catch (error) {
      authLogger.error(`${provider} callback handling failed`, { error });
      throw error;
    }
  }

  async handleOktaCallback(code: string): Promise<UserInfo> {
    return this.handleSSOCallbackGeneric(
      code,
      'okta',
      'okta_state',
      'okta_verifier',
      this.exchangeCodeForTokens.bind(this),
      '/api/v1/auth/okta',
      (tokens: any) => ({ id_token: tokens.id_token }),
    );
  }

  async handleMicrosoftCallback(code: string): Promise<UserInfo> {
    const redirectUri = `${window.location.origin}/auth/microsoft/callback`;

    return this.handleSSOCallbackGeneric(
      code,
      'microsoft',
      'microsoft_state',
      'microsoft_verifier',
      // For Microsoft, the backend handles the actual exchange for tokens to fix CORS issues.
      // We just pass the code and verifier through to the generic handler's request builder.
      async (code, verifier) => ({ code, verifier }),
      '/api/v1/auth/microsoft',
      (data: any) => ({
        code: data.code,
        code_verifier: data.verifier,
        redirect_uri: redirectUri,
      }),
    );
  }

  /**
   * Generic token exchange method that works for any OAuth provider
   * Eliminates code duplication between Okta and Microsoft token exchange
   */
  private async genericTokenExchange(
    tokenUrl: string,
    params: URLSearchParams,
    providerName: string,
  ): Promise<any> {
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      authLogger.error(`${providerName} token exchange failed`, {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
      });
      throw new AuthError(
        `${providerName} token exchange failed: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`,
      );
    }

    const tokens = await response.json();
    authLogger.info(`Successfully exchanged ${providerName} authorization code for tokens`);
    return tokens;
  }

  private async exchangeCodeForTokens(code: string, codeVerifier: string): Promise<any> {
    // For company-based SSO, use stored provider info instead of environment variables
    const storedProviderInfo = sessionStorage.getItem('sso_provider_info');
    let oktaDomain: string;
    let oktaClientId: string;
    let oktaAuthServer: string;
    let redirectUri: string;

    if (storedProviderInfo) {
      // Company-based SSO: use database configuration
      try {
        const providerInfo = JSON.parse(storedProviderInfo);
        oktaDomain = providerInfo.domain;
        oktaClientId = providerInfo.client_id;
        oktaAuthServer = providerInfo.auth_server || 'default';
        redirectUri = providerInfo.redirect_uri || `${window.location.origin}/auth/okta/callback`;

        authLogger.info('Using company SSO configuration for token exchange', {
          domain: oktaDomain,
          clientId: oktaClientId.substring(0, 8) + '...',
          authServer: oktaAuthServer,
        });
      } catch (e) {
        authLogger.error('Failed to parse stored provider info', { error: e });
        throw new AuthError('Invalid SSO provider configuration.');
      }
    } else {
      // Legacy hardcoded Okta SSO: use environment variables
      oktaDomain = ENV.VITE_OKTA_DOMAIN;
      oktaClientId = ENV.VITE_OKTA_CLIENT_ID;
      oktaAuthServer = ENV.VITE_OKTA_AUTH_SERVER || 'default';
      redirectUri = ENV.VITE_OKTA_REDIRECT_URI || `${window.location.origin}/auth/okta/callback`;

      authLogger.info('Using legacy Okta configuration for token exchange');
    }

    // Validate required configuration
    if (!oktaDomain || !oktaClientId) {
      throw new AuthError('Okta configuration is incomplete. Missing domain or client ID.');
    }

    const tokenUrl = `https://${oktaDomain}/oauth2/${oktaAuthServer}/v1/token`;

    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: oktaClientId,
      code: code,
      code_verifier: codeVerifier,
      redirect_uri: redirectUri,
    });

    return this.genericTokenExchange(tokenUrl, params, 'Okta');
  }

  async logout(): Promise<void> {
    const startTime = performance.now();
    const currentUserId = this._user?.id;
    const currentEmail = this._user?.email;

    try {
      // Call logout endpoint (mainly for logging purposes with JWT)
      await apiClient.post('/api/v1/auth/logout', {}, AUTH_HTTP_OPTIONS);

      // Sign out from auth provider
      const { error } = await this.authProvider.signOut();
      if (error) {
        authLogger.info('Provider signout error', { error });
      }

      // Track successful logout
      securityEventTracker.trackAuthEvent('logout', true, currentEmail, undefined, {
        userId: currentUserId,
        logoutType: 'user_initiated',
      });

      securityEventTracker.trackPerformance('logout', startTime, true, {
        userId: currentUserId,
      });

      this.cleanup();
    } catch (error: unknown) {
      // Track logout error
      const errorMessage = error instanceof Error ? error.message : 'Unknown logout error';

      securityEventTracker.trackAuthEvent('logout', false, currentEmail, errorMessage, {
        userId: currentUserId,
        logoutType: 'user_initiated',
        errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
      });

      securityEventTracker.trackPerformance('logout', startTime, false, {
        userId: currentUserId,
        error: errorMessage,
      });

      // Even if backend logout fails, clean up local state
      this.cleanup();
      throw handleError(error);
    }
  }

  async getCurrentUser(): Promise<UserInfo> {
    try {
      if (this._user && this._session) {
        // For recently authenticated users (within 2 minutes), skip API validation
        // to avoid issues with newly created tokens
        const now = Date.now();
        const isRecentlyAuthenticated =
          this.recentlyAuthenticated && now - this.lastAuthTime < 120000; // 2 minutes

        // If we have persisted user data, consider it valid unless we need to validate
        // Skip API validation for recently authenticated users or if we have valid cached data
        if (isRecentlyAuthenticated) {
          authLogger.debug('Skipping API validation for recently authenticated user');
          this.resetCorruptedTokenCounter();
          return { user: this._user, session: this._session };
        }

        // For persisted users (not recently authenticated), try API validation
        // but don't fail if cookies aren't ready yet
        try {
          const response = await apiClient.get('/api/v1/auth/me', AUTH_HTTP_OPTIONS);
          // If request succeeds, we have valid auth state
          this.resetCorruptedTokenCounter();

          // Sync CSRF token from response if provided
          this._syncCsrfToken(response.data.csrf_token, 'active session');

          return { user: this._user, session: this._session };
        } catch (error: unknown) {
          // Check if this is a 401 (invalid tokens) vs network/other error
          const isAuthError =
            error &&
            typeof error === 'object' &&
            'response' in error &&
            (error as any).response?.status === 401;

          if (isAuthError) {
            // Tokens are invalid - clear state
            authLogger.warn('API validation failed with 401, clearing auth state', { error });
            this.cleanup();
            triggerAuthError();
            return { user: null, session: null };
          } else {
            // Network error or other issue - keep cached auth state for now
            authLogger.warn('API validation failed (non-401), keeping cached auth state', {
              error,
            });
            return { user: this._user, session: this._session };
          }
        }
      }

      // Try to get user from API (tokens are in HTTP-only cookies)
      // If we have user info but no session, validate by calling /auth/me
      if (this._user && !this._session) {
        try {
          const response = await apiClient.get('/api/v1/auth/me', AUTH_HTTP_OPTIONS);
          // If request succeeds, restore auth state from response
          this.resetCorruptedTokenCounter();
          const userData = response.data.user;
          this._user = userData;

          // Sync CSRF token from response if provided
          this._syncCsrfToken(response.data.csrf_token, 'startup');

          this._session = {
            access_token: '', // Not accessible from JavaScript
            token_type: 'Bearer',
            user: userData,
          };

          // Hydrate in-memory token for direct API calls if missing.
          // Use backend token bridge instead of refresh endpoint to avoid
          // unnecessary refresh attempts during startup/session checks.
          if (!this._accessToken) {
            authLogger.info('Hydrating in-memory access token on startup via token bridge');
            const token = await this.fetchAccessTokenFromBackend().catch((e) => {
              authLogger.warn('Startup token bridge hydration failed', { error: e });
              return null;
            });
            if (!token) {
              authLogger.warn('Startup token bridge returned no access token');
            }
          }

          return { user: this._user, session: this._session };
        } catch (error: unknown) {
          // Token is invalid, expired, or corrupted - try to refresh before giving up
          authLogger.warn('Token validation failed, attempting refresh', { error });
          try {
            await this.refreshAccessTokenInternal();
            // Refresh succeeded, try to get user info again
            const retryResponse = await apiClient.get('/api/v1/auth/me', AUTH_HTTP_OPTIONS);
            const userData = retryResponse.data.user;
            this._user = userData;

            // Sync CSRF token from response if provided
            this._syncCsrfToken(retryResponse.data.csrf_token, 'after refresh retry');

            this._session = {
              access_token: '', // Not accessible from JavaScript
              token_type: 'Bearer',
              user: userData,
            };
            return { user: this._user, session: this._session };
          } catch (refreshError: unknown) {
            // Both validation and refresh failed, clear state and trigger callback
            authLogger.info('Token refresh failed after validation failure, clearing auth state');
            this.cleanup();
            triggerAuthError();
          }
        }
      }

      // No in-memory auth state yet; validate directly via cookie-backed API.
      try {
        const response = await apiClient.get('/api/v1/auth/me', AUTH_HTTP_OPTIONS);
        const userData = response.data.user;

        if (!userData) {
          return { user: null, session: null };
        }

        this._user = userData;
        this._syncCsrfToken(response.data.csrf_token, 'cold start');
        this._session = {
          access_token: '', // Not accessible from JavaScript
          token_type: 'Bearer',
          user: userData,
        };

        // Hydrate in-memory token for direct API calls if missing.
        // Use backend token bridge instead of refresh endpoint to avoid
        // unnecessary refresh attempts during cold start.
        if (!this._accessToken) {
          const token = await this.fetchAccessTokenFromBackend().catch((e) => {
            authLogger.warn('Cold-start token bridge hydration failed', { error: e });
            return null;
          });
          if (!token) {
            authLogger.warn('Cold-start token bridge returned no access token');
          }
        }

        return { user: this._user, session: this._session };
      } catch (error: unknown) {
        const isAuthError =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          ((error as any).response?.status === 401 || (error as any).response?.status === 403);

        if (isAuthError) {
          return { user: null, session: null };
        }

        // Non-auth failures should not hard-fail startup auth resolution.
        authLogger.warn('Unable to resolve user from /auth/me on cold start', { error });
        return { user: null, session: null };
      }
    } catch (error: unknown) {
      throw new AuthError('Failed to get current user', error);
    }
  }

  async refreshSession(): Promise<UserInfo> {
    try {
      await this.refreshAccessTokenInternal();
      return { user: this._user, session: this._session };
    } catch (error: unknown) {
      this.cleanup();
      throw handleError(error);
    }
  }

  async syncWithBackend(): Promise<void> {
    try {
      // Backend sync is now automatic via the JWT token
      // The user ID is embedded in the JWT token
      authLogger.info('JWT token ready for backend sync');
    } catch (error: unknown) {
      authLogger.warn('Backend sync warning', { error });
      // Don't throw error as this shouldn't break the auth flow
    }
  }

  getBackendUserId(): string | null {
    return this._user?.id || null;
  }

  /**
   * Reads CSRF token from document.cookie and synchronizes it with internal state.
   * Uses the last token found to mirror server-side cookie parsing behavior.
   */
  private _loadCsrfTokenFromCookie(): void {
    const token = getCsrfTokenFromDocumentCookie(authLogger);
    if (token) {
      this._csrfToken = token;
      authLogger.debug('CSRF token loaded from cookie on startup');
    }
  }

  isAuthenticated(): boolean {
    // Check if we have user info (tokens are in HTTP-only cookies)
    // Actual token validation happens on the backend
    return !!this._user;
  }
}

// Create singleton instance
const authService = new CentralizedAuthService();

export default authService;
